import React, { useState, useEffect } from 'react';
import { 
  BuildingOfficeIcon, 
  TruckIcon, 
  UserGroupIcon
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

interface Project {
  id: number;
  title: string;
  client: string;
  image: string;
  description: string;
  category: string;
  status?: string;
  is_featured?: boolean;
}

const Projects: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState('All');

  // Fetch projects from API
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://plencoplumbingltd.com/api/projects');
        
        if (!response.ok) {
          throw new Error('Failed to fetch projects');
        }
        
        const result = await response.json();
        
        if (result.success && result.data) {
          setProjects(result.data);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        console.error('Error fetching projects:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Extract unique categories
  const categories = ['All', ...Array.from(new Set(projects.map(p => p.category)))];
  
  // Filter projects by category
  const filteredProjects = activeCategory === 'All' 
    ? projects 
    : projects.filter(p => p.category === activeCategory);

  const achievements = [
    {
      icon: BuildingOfficeIcon,
      color: 'blue',
      title: 'Regional Excellence',
      description: 'Delivering efficient construction and plumbing services through our associate offices to clients outside Uganda'
    },
    {
      icon: TruckIcon,
      color: 'green',
      title: 'Logistics Expertise',
      description: 'Successfully handled transportation of conventional materials both locally and across the region'
    },
    {
      icon: UserGroupIcon,
      color: 'orange',
      title: 'Expert Team',
      description: 'Evolved with a highly effective team of expertise and networks for providing door to door services'
    }
  ];

  return (
    <div className="bg-white">
      <SEO 
        title="Projects - Our Portfolio | Plenco Plumbing & Construction Uganda"
        description="Explore Plenco's completed construction and plumbing projects across Uganda and East Africa. Residential buildings, commercial complexes, fuel stations, tank calibration projects, renovation works, and more. Quality workmanship and timely delivery since 2013."
        keywords="construction projects Uganda, plumbing projects Kampala, completed projects East Africa, fuel station projects, building construction portfolio, tank calibration projects, renovation portfolio Uganda"
        canonicalUrl="http://www.plencoplumbingltd.com/projects"
        type="website"
      />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-primary-700 text-white pt-32 sm:pt-36 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-black mb-4">
            <span className="text-white">Our</span>{' '}
            <span className="text-primary-200">Projects</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-primary-100 max-w-3xl mx-auto">
            A showcase of our completed works across Uganda and East Africa
          </p>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-20 bg-gradient-to-br from-primary-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black mb-4">
              <span className="text-primary">Our</span>{' '}
              <span className="text-gray-600">Achievements</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-4xl mx-auto leading-relaxed">
              We have consistently demonstrated a collaborative approach of working with customers to offer 
              best construction solutions tailored to meet their individual needs. Our working methodology 
              has enabled us to create a benchmark in the plumbing and building industry.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            {achievements.map((achievement, index) => {
              const Icon = achievement.icon;
              const colorMap: { [key: string]: string } = {
                blue: 'bg-blue-100 text-blue-600',
                green: 'bg-green-100 text-green-600',
                orange: 'bg-orange-100 text-orange-600'
              };
              return (
                <div key={index} className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className={`w-16 h-16 rounded-lg ${colorMap[achievement.color]} flex items-center justify-center mb-4`}>
                    <Icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{achievement.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{achievement.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Projects Showcase Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black mb-4">
              <span className="text-primary">Featured</span>{' '}
              <span className="text-gray-600">Projects</span>
            </h2>
            <p className="text-gray-600 text-lg">Delivering excellence across diverse project types</p>
          </div>

          {loading && (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary"></div>
              <p className="mt-4 text-gray-600 text-lg">Loading projects...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-20">
              <div className="bg-red-50 border border-red-200 rounded-lg p-8 max-w-lg mx-auto">
                <p className="text-red-600 text-lg font-semibold mb-2">Error Loading Projects</p>
                <p className="text-red-500">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && filteredProjects.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">No projects found in this category.</p>
            </div>
          )}

          {!loading && !error && filteredProjects.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project, index) => {
                const borderColors = ['border-blue-500', 'border-green-500', 'border-orange-500', 'border-purple-500', 'border-pink-500', 'border-indigo-500'];
                const borderColor = borderColors[index % borderColors.length];
                
                return (
                  <div 
                    key={project.id} 
                    className={`bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-t-4 ${borderColor}`}
                  >
                    <div className="relative h-64 bg-gray-200">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = 'https://via.placeholder.com/600x400/035e85/ffffff?text=' + encodeURIComponent(project.title);
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                      <div className="absolute top-4 right-4 bg-primary text-white px-3 py-1 rounded-full text-xs font-semibold">
                        {project.category}
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                    <p className="text-primary font-semibold text-sm mb-3">{project.client}</p>
                    <p className="text-gray-600 leading-relaxed">{project.description}</p>
                  </div>
                </div>
              );
            })}
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-gray-50 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mb-3 sm:mb-4">Our Track Record</h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600">Numbers that speak for our experience and dedication</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md border-l-4 border-primary">
              <div className="text-3xl sm:text-4xl md:text-5xl font-black text-primary mb-2">500+</div>
              <div className="text-xs sm:text-sm md:text-base text-gray-600 font-semibold">Projects Completed</div>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md border-l-4 border-blue-500">
              <div className="text-3xl sm:text-4xl md:text-5xl font-black text-blue-500 mb-2">10+</div>
              <div className="text-xs sm:text-sm md:text-base text-gray-600 font-semibold">Years Experience</div>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md border-l-4 border-green-500">
              <div className="text-3xl sm:text-4xl md:text-5xl font-black text-green-500 mb-2">36+</div>
              <div className="text-xs sm:text-sm md:text-base text-gray-600 font-semibold">Team Members</div>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md border-l-4 border-orange-500">
              <div className="text-3xl sm:text-4xl md:text-5xl font-black text-orange-500 mb-2">4</div>
              <div className="text-xs sm:text-sm md:text-base text-gray-600 font-semibold">Countries Served</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-primary to-primary-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-black mb-6">Have a Project in Mind?</h2>
          <p className="text-xl mb-8 text-primary-100">
            Let's discuss how we can help bring your project to life
          </p>
          <a
            href="/contact"
            className="inline-block bg-white text-primary px-8 py-4 rounded-lg font-semibold hover:bg-primary-50 transition-colors shadow-lg"
          >
            Start Your Project
          </a>
        </div>
      </section>
    </div>
  );
};

export default Projects;
